import React, { useState, useMemo } from 'react';
import ExcelJS from 'exceljs';
import { useAuth } from '../hooks/useAuth';
import tareoService from '../services/tareoService';
import reporteService from '../services/reporteService';

const fechaHoyLima = () =>
  new Date().toLocaleDateString('en-CA', { timeZone: 'America/Lima' });

const formatearFecha = (fecha, vacio = '—') => {
  if (!fecha) return vacio;
  const texto = String(fecha).split('T')[0];
  const [y, m, d] = texto.split('-');
  if (!y || !m || !d) return texto;
  return `${d}/${m}/${y}`;
};

/** Convierte filas largas (empleado × concepto) en tabla dinámica horizontal. */
function pivotConceptosIngreso(filasLong) {
  if (!filasLong?.length) return { columnas: [], filas: [] };

  const columnasMap = new Map();
  const empleadosMap = new Map();

  filasLong.forEach((row) => {
    if (!empleadosMap.has(row.empleado_id)) {
      empleadosMap.set(row.empleado_id, {
        empleado_id: row.empleado_id,
        numero_documento: row.numero_documento,
        nombre_completo: row.nombre_completo,
        cargo: row.cargo,
        proyecto: row.proyecto,
        tipo_contratacion: row.tipo_contratacion,
        modalidad_pago: row.modalidad_pago || (row.es_recibo_honorarios ? 'RH' : 'Planilla'),
        es_recibo_honorarios: !!row.es_recibo_honorarios,
        regimen_laboral_codigo: row.regimen_laboral_codigo,
        regimen_laboral_nombre: row.regimen_laboral_nombre,
        sueldo_basico: row.sueldo_basico,
        sin_conceptos: !!row.sin_conceptos,
        montos: {},
      });
    }

    const emp = empleadosMap.get(row.empleado_id);
    if (row.sin_conceptos || row.codigo_plame == null) return;

    const key = String(row.codigo_plame);
    if (!columnasMap.has(key)) {
      columnasMap.set(key, {
        key,
        codigo_plame: row.codigo_plame,
        concepto_descripcion: row.concepto_descripcion,
      });
    }
    emp.montos[key] = row.monto;
    emp.sin_conceptos = false;
  });

  const columnas = Array.from(columnasMap.values()).sort((a, b) =>
    String(a.codigo_plame).localeCompare(String(b.codigo_plame), 'es')
  );

  const filas = Array.from(empleadosMap.values()).sort((a, b) =>
    (a.nombre_completo || '').localeCompare(b.nombre_completo || '', 'es')
  );

  return { columnas, filas };
}

// Mismo mapeo de eventos que en Tareos (tipo_evento -> etiqueta corta para tabla)
const EVENTOS_TAREOS = [
  { key: 'asistio', label: 'Asistió', class: 'text-green-600' },
  { key: 'falta', label: 'Falta', class: 'text-red-600' },
  { key: 'feriado', label: 'Feriado', class: 'text-gray-600' },
  { key: 'feriado_laborado', label: 'Feriado lab.', class: 'text-blue-600' },
  { key: 'descanso', label: 'Descanso', class: 'text-gray-600' },
  { key: 'descanso_laborado', label: 'Descanso lab.', class: 'text-blue-600' },
  { key: 'permiso', label: 'Permiso', class: 'text-amber-600' },
  { key: 'vacaciones', label: 'Vacaciones', class: 'text-cyan-600' },
  { key: 'descanso_medico', label: 'Desc. médico', class: 'text-purple-600' },
  { key: 'reposo_medico', label: 'Reposo méd.', class: 'text-purple-600' },
  { key: 'subsidio_por_enfermedad', label: 'Subsidio', class: 'text-purple-600' },
];

const Reportes = () => {
  const { selectedCompany } = useAuth();
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resumen, setResumen] = useState(null);

  // Reporte por género (apartado propio)
  const [fechaDesdeGenero, setFechaDesdeGenero] = useState('');
  const [fechaHastaGenero, setFechaHastaGenero] = useState('');
  const [loadingGenero, setLoadingGenero] = useState(false);
  const [errorGenero, setErrorGenero] = useState(null);
  const [resumenGenero, setResumenGenero] = useState(null);
  const [periodoGenero, setPeriodoGenero] = useState(null);

  // Trabajadores activos — régimen pensionario
  const [fechaCortePension, setFechaCortePension] = useState(fechaHoyLima);
  const [loadingPension, setLoadingPension] = useState(false);
  const [errorPension, setErrorPension] = useState(null);
  const [reportePension, setReportePension] = useState(null);

  // Conceptos de ingreso en boleta por trabajador
  const [fechaCorteIngresos, setFechaCorteIngresos] = useState(fechaHoyLima);
  const [loadingIngresos, setLoadingIngresos] = useState(false);
  const [errorIngresos, setErrorIngresos] = useState(null);
  const [reporteIngresos, setReporteIngresos] = useState(null);

  // Contratos pendientes de firma
  const [loadingContratosFirma, setLoadingContratosFirma] = useState(false);
  const [errorContratosFirma, setErrorContratosFirma] = useState(null);
  const [reporteContratosFirma, setReporteContratosFirma] = useState(null);

  const pivotIngresos = useMemo(
    () => pivotConceptosIngreso(reporteIngresos?.filas ?? []),
    [reporteIngresos]
  );

  const handleGenerarResumenTareos = async () => {
    if (!selectedCompany?.id) {
      setError('Selecciona una empresa para generar el reporte.');
      return;
    }
    if (!fechaDesde || !fechaHasta) {
      setError('Indica el rango de fechas (desde y hasta).');
      return;
    }
    if (fechaHasta < fechaDesde) {
      setError('La fecha hasta debe ser igual o posterior a la fecha desde.');
      return;
    }
    setError(null);
    setLoading(true);
    setResumen(null);
    try {
      const response = await tareoService.obtenerResumenPorPeriodo(
        fechaDesde,
        fechaHasta,
        selectedCompany.id
      );
      if (response?.success && response?.data) {
        setResumen(response.data);
      } else {
        setError(response?.message || 'No se pudo obtener el resumen.');
      }
    } catch (err) {
      const msg =
        err.isReporteTimeout
          ? err.message
          : err.response?.data?.message || err.message || 'Error al generar el reporte.';
      setError(msg);
      setResumen(null);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerarResumenGenero = async () => {
    if (!selectedCompany?.id) {
      setErrorGenero('Selecciona una empresa para generar el reporte.');
      return;
    }
    if (!fechaDesdeGenero || !fechaHastaGenero) {
      setErrorGenero('Indica el rango de fechas (desde y hasta).');
      return;
    }
    if (fechaHastaGenero < fechaDesdeGenero) {
      setErrorGenero('La fecha hasta debe ser igual o posterior a la fecha desde.');
      return;
    }
    setErrorGenero(null);
    setLoadingGenero(true);
    setResumenGenero(null);
    setPeriodoGenero(null);
    try {
      const response = await tareoService.obtenerResumenPorPeriodo(
        fechaDesdeGenero,
        fechaHastaGenero,
        selectedCompany.id
      );
      if (response?.success && response?.data) {
        setResumenGenero(response.data.resumen_genero ?? null);
        setPeriodoGenero(response.data.periodo ?? null);
      } else {
        setErrorGenero(response?.message || 'No se pudo obtener el resumen.');
      }
    } catch (err) {
      const msg =
        err.isReporteTimeout
          ? err.message
          : err.response?.data?.message || err.message || 'Error al generar el reporte.';
      setErrorGenero(msg);
      setResumenGenero(null);
      setPeriodoGenero(null);
    } finally {
      setLoadingGenero(false);
    }
  };

  const formatNumber = (n) => (n != null ? Number(n).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00');

  const formatMonto = (n) => (n != null && n !== '' ? formatNumber(n) : '—');

  const handleGenerarReporteIngresos = async () => {
    if (!selectedCompany?.id) {
      setErrorIngresos('Selecciona una empresa para generar el reporte.');
      return;
    }
    if (!fechaCorteIngresos) {
      setErrorIngresos('Indica la fecha de corte.');
      return;
    }
    setErrorIngresos(null);
    setLoadingIngresos(true);
    setReporteIngresos(null);
    try {
      const response = await reporteService.obtenerConceptosIngresoBoleta(
        selectedCompany.id,
        fechaCorteIngresos
      );
      if (response?.success && response?.data) {
        setReporteIngresos(response.data);
      } else {
        setErrorIngresos(response?.message || 'No se pudo obtener el reporte.');
      }
    } catch (err) {
      setErrorIngresos(err.response?.data?.message || err.message || 'Error al generar el reporte.');
      setReporteIngresos(null);
    } finally {
      setLoadingIngresos(false);
    }
  };

  const exportarReporteIngresosExcel = async () => {
    if (!reporteIngresos?.filas?.length) return;
    const { columnas, filas: filasPivot } = pivotConceptosIngreso(reporteIngresos.filas);
    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'Sistema Planillas';

    const sheetPivot = workbook.addWorksheet('Tabla dinámica ingresos', {
      views: [{ state: 'frozen', ySplit: 5, xSplit: 4 }],
    });
    sheetPivot.addRow(['Conceptos de ingreso en boleta — tabla dinámica']).font = { bold: true, size: 14 };
    sheetPivot.addRow([`Fecha de corte: ${reporteIngresos.fecha_corte ?? ''}`]);
    sheetPivot.addRow([
      `Trabajadores: ${reporteIngresos.totales?.empleados ?? 0}`,
      `Planilla: ${reporteIngresos.totales?.empleados_planilla ?? 0}`,
      `RH: ${reporteIngresos.totales?.empleados_rh ?? 0}`,
      `Conceptos PLAME: ${columnas.length}`,
    ]);
    sheetPivot.addRow([]);

    const headersPivot = [
      'DNI',
      'Trabajador',
      'Modalidad',
      'Régimen laboral',
      ...columnas.map((c) => `${c.codigo_plame} — ${c.concepto_descripcion || ''}`.trim()),
    ];
    const headerPivot = sheetPivot.addRow(headersPivot);
    headerPivot.font = { bold: true };
    headerPivot.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE5E7E7' } };

    filasPivot.forEach((emp) => {
      const regimen = emp.regimen_laboral_codigo
        ? `${emp.regimen_laboral_codigo}${emp.regimen_laboral_nombre ? ` — ${emp.regimen_laboral_nombre}` : ''}`
        : emp.regimen_laboral_nombre || '';
      sheetPivot.addRow([
        emp.numero_documento ?? '',
        emp.nombre_completo ?? '',
        emp.modalidad_pago ?? 'Planilla',
        regimen,
        ...columnas.map((col) => {
          const m = emp.montos[col.key];
          return m != null ? Number(m) : '';
        }),
      ]);
    });

    sheetPivot.columns = [
      { width: 14 },
      { width: 38 },
      { width: 12 },
      { width: 22 },
      ...columnas.map(() => ({ width: 16 })),
    ];

    const sheetDetalle = workbook.addWorksheet('Detalle vertical', {
      views: [{ state: 'frozen', ySplit: 4 }],
    });
    sheetDetalle.addRow(['Conceptos de ingreso — detalle vertical']).font = { bold: true, size: 14 };
    sheetDetalle.addRow([`Fecha de corte: ${reporteIngresos.fecha_corte ?? ''}`]);
    sheetDetalle.addRow([]);

    const headersDetalle = [
      'DNI',
      'Trabajador',
      'Modalidad',
      'Tipo contratación',
      'Cargo',
      'Proyecto',
      'Régimen laboral',
      'Cód. PLAME',
      'Concepto de ingreso',
      'Monto',
    ];
    const headerDetalle = sheetDetalle.addRow(headersDetalle);
    headerDetalle.font = { bold: true };
    headerDetalle.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE5E7E7' } };

    reporteIngresos.filas.forEach((row) => {
      const regimen = row.regimen_laboral_codigo
        ? `${row.regimen_laboral_codigo}${row.regimen_laboral_nombre ? ` — ${row.regimen_laboral_nombre}` : ''}`
        : row.regimen_laboral_nombre || '—';
      sheetDetalle.addRow([
        row.numero_documento ?? '',
        row.nombre_completo ?? '',
        row.modalidad_pago ?? (row.es_recibo_honorarios ? 'RH' : 'Planilla'),
        row.tipo_contratacion ?? '',
        row.cargo ?? '',
        row.proyecto ?? '',
        regimen,
        row.codigo_plame ?? (row.sin_conceptos ? '—' : ''),
        row.sin_conceptos ? '(Sin conceptos de ingreso)' : (row.concepto_descripcion ?? ''),
        row.monto != null ? Number(row.monto) : '',
      ]);
    });

    sheetDetalle.columns = [
      { width: 14 },
      { width: 38 },
      { width: 12 },
      { width: 22 },
      { width: 26 },
      { width: 30 },
      { width: 28 },
      { width: 12 },
      { width: 36 },
      { width: 14 },
    ];

    const buf = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buf], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Conceptos_Ingreso_Boleta_${reporteIngresos.fecha_corte ?? 'corte'}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleGenerarReportePension = async () => {
    if (!selectedCompany?.id) {
      setErrorPension('Selecciona una empresa para generar el reporte.');
      return;
    }
    if (!fechaCortePension) {
      setErrorPension('Indica la fecha de corte.');
      return;
    }
    setErrorPension(null);
    setLoadingPension(true);
    setReportePension(null);
    try {
      const response = await reporteService.obtenerTrabajadoresActivosPension(
        selectedCompany.id,
        fechaCortePension
      );
      if (response?.success && response?.data) {
        setReportePension(response.data);
      } else {
        setErrorPension(response?.message || 'No se pudo obtener el reporte.');
      }
    } catch (err) {
      setErrorPension(err.response?.data?.message || err.message || 'Error al generar el reporte.');
      setReportePension(null);
    } finally {
      setLoadingPension(false);
    }
  };

  const exportarReportePensionExcel = async () => {
    if (!reportePension?.empleados?.length) return;
    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'Sistema Planillas';
    const sheet = workbook.addWorksheet('Activos pensión', { views: [{ state: 'frozen', ySplit: 4 }] });

    sheet.addRow(['Trabajadores activos — régimen pensionario']).font = { bold: true, size: 14 };
    sheet.addRow([`Fecha de corte: ${reportePension.fecha_corte ?? ''}`]);
    sheet.addRow([
      `Total: ${reportePension.totales?.total ?? 0}`,
      `ONP: ${reportePension.totales?.onp ?? 0}`,
      `AFP: ${reportePension.totales?.afp ?? 0}`,
    ]);
    sheet.addRow([]);

    const headers = [
      'N°',
      'DNI',
      'Trabajador',
      'Cargo',
      'Proyecto',
      'Régimen',
      'AFP',
      'Tipo AFP',
      'CUSPP',
    ];
    const headerRow = sheet.addRow(headers);
    headerRow.font = { bold: true };
    headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE5E7E7' } };

    reportePension.empleados.forEach((emp, idx) => {
      sheet.addRow([
        idx + 1,
        emp.numero_documento ?? '',
        emp.nombre_completo ?? '',
        emp.cargo ?? '',
        emp.proyecto ?? '',
        emp.regimen_pensionario ?? emp.sistema_pensionario ?? '',
        emp.afp_nombre ?? '—',
        emp.tipo_afp ?? '—',
        emp.cuspp ?? '—',
      ]);
    });

    sheet.columns = [
      { width: 6 },
      { width: 14 },
      { width: 38 },
      { width: 28 },
      { width: 32 },
      { width: 22 },
      { width: 18 },
      { width: 12 },
      { width: 16 },
    ];

    const buf = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buf], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Activos_Pension_${reportePension.fecha_corte ?? 'corte'}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleGenerarContratosPendientesFirma = async () => {
    if (!selectedCompany?.id) {
      setErrorContratosFirma('Selecciona una empresa para generar el reporte.');
      return;
    }
    setErrorContratosFirma(null);
    setLoadingContratosFirma(true);
    setReporteContratosFirma(null);
    try {
      const response = await reporteService.obtenerContratosPendientesFirma(selectedCompany.id);
      if (response?.success && response?.data) {
        setReporteContratosFirma(response.data);
      } else {
        setErrorContratosFirma(response?.message || 'No se pudo obtener el reporte.');
      }
    } catch (err) {
      setErrorContratosFirma(
        err.response?.data?.message || err.message || 'Error al generar el reporte.'
      );
      setReporteContratosFirma(null);
    } finally {
      setLoadingContratosFirma(false);
    }
  };

  const exportarContratosPendientesFirmaExcel = async () => {
    if (!reporteContratosFirma?.filas?.length) return;
    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'Sistema Planillas';
    const sheet = workbook.addWorksheet('Pendientes de firma', {
      views: [{ state: 'frozen', ySplit: 4 }],
    });

    sheet.addRow(['Contratos pendientes de firma']).font = { bold: true, size: 14 };
    sheet.addRow([`Fecha de corte: ${formatearFecha(reporteContratosFirma.fecha_corte)}`]);
    sheet.addRow([
      `Trabajadores: ${reporteContratosFirma.totales?.trabajadores ?? 0}`,
      `Contratos: ${reporteContratosFirma.totales?.contratos ?? 0}`,
    ]);
    sheet.addRow([]);

    const headers = [
      'N°',
      'Documento de identidad',
      'Nombre completo',
      'Cargo o puesto',
      'Fecha de ingreso',
      'Tipo de contrato',
      'Nombre del contrato',
      'Fecha de inicio del contrato',
      'Fecha de término',
      'Estado del contrato',
      'Estado de firma',
    ];
    const headerRow = sheet.addRow(headers);
    headerRow.font = { bold: true };
    headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE5E7E7' } };

    reporteContratosFirma.filas.forEach((row, idx) => {
      sheet.addRow([
        idx + 1,
        row.documento_identidad || row.numero_documento || '',
        row.nombre_completo ?? '',
        row.cargo ?? '',
        formatearFecha(row.fecha_ingreso, ''),
        row.tipo_contrato ?? '',
        row.titulo ?? '',
        formatearFecha(row.fecha_inicio, ''),
        formatearFecha(row.fecha_termino, ''),
        row.estado_contrato ?? '',
        row.estado_firma ?? 'Pendiente',
      ]);
    });

    sheet.columns = [
      { width: 6 },
      { width: 22 },
      { width: 38 },
      { width: 28 },
      { width: 16 },
      { width: 28 },
      { width: 36 },
      { width: 22 },
      { width: 18 },
      { width: 20 },
      { width: 18 },
    ];

    const buf = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buf], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Contratos_Pendientes_Firma_${reporteContratosFirma.fecha_corte ?? fechaHoyLima()}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportarResumenGeneroExcel = async () => {
    if (!resumenGenero) return;
    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'Sistema Planillas';
    const sheet = workbook.addWorksheet('Resumen por género', { views: [{ state: 'frozen', ySplit: 4 }] });

    sheet.addRow(['Resumen por género']).font = { bold: true, size: 14 };
    sheet.addRow([`Período: ${periodoGenero?.fecha_inicio ?? ''} — ${periodoGenero?.fecha_fin ?? ''}`]);
    sheet.addRow([]);
    sheet.addRow(['Resumen', 'Hombres', resumenGenero.hombres ?? 0]);
    sheet.addRow(['', 'Mujeres', resumenGenero.mujeres ?? 0]);
    if ((resumenGenero.sin_dato ?? 0) > 0) {
      sheet.addRow(['', 'Sin especificar', resumenGenero.sin_dato]);
    }
    sheet.addRow([]);

    const detalleHeaders = ['Trabajador', 'Cargo', 'Proyecto'];
    const addDetalleTable = (titulo, filas) => {
      if (!filas || filas.length === 0) return;
      sheet.addRow([titulo]).font = { bold: true, size: 12 };
      const headerRow = sheet.addRow(detalleHeaders);
      headerRow.font = { bold: true };
      headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE5E7E7' } };
      filas.forEach((row) => {
        sheet.addRow([row.nombre_completo ?? '', row.cargo ?? '', row.proyecto ?? '']);
      });
      sheet.addRow([]);
    };

    addDetalleTable('Hombres — detalle', resumenGenero.detalle?.hombres);
    addDetalleTable('Mujeres — detalle', resumenGenero.detalle?.mujeres);
    addDetalleTable('Sin especificar — detalle', resumenGenero.detalle?.sin_dato);

    sheet.columns = [{ width: 35 }, { width: 25 }, { width: 40 }];

    const buf = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Resumen_Genero_${periodoGenero?.fecha_inicio ?? 'inicio'}_${periodoGenero?.fecha_fin ?? 'fin'}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportarResumenExcel = async () => {
    if (!resumen || !resumen.empleados || resumen.empleados.length === 0) return;
    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'Sistema Planillas';
    const sheet = workbook.addWorksheet('Resumen Tareos', { views: [{ state: 'frozen', ySplit: 3 }] });

    const headers = [
      'Trabajador',
      'Cargo',
      'Proyecto',
      'Registros',
      'Horas efectuadas',
      'Horas desc. médico',
      'Horas otros',
      'Prom. h/día',
      ...EVENTOS_TAREOS.map((e) => e.label),
      'Tardanzas',
      'Total conceptos',
    ];

    sheet.addRow(['Resumen de Tareos por Fecha Personalizada']).font = { bold: true, size: 14 };
    sheet.addRow([
      `Período: ${resumen.periodo?.fecha_inicio || ''} — ${resumen.periodo?.fecha_fin || ''}`,
      `Días: ${resumen.periodo?.dias_periodo ?? 0}`,
      `Registros: ${resumen.totales?.total_registros ?? 0}`,
      `Horas totales: ${formatNumber(resumen.totales?.total_horas_trabajadas)} (efect. ${formatNumber(resumen.totales?.total_horas_efectuadas)} / desc. méd. ${formatNumber(resumen.totales?.total_horas_descanso_medico)} / otros ${formatNumber(resumen.totales?.total_horas_otros_conceptos)})`,
    ]);
    sheet.addRow([]);
    const headerRow = sheet.addRow(headers);
    headerRow.font = { bold: true };
    headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE5E7E7' } };

    resumen.empleados.forEach((emp) => {
      const porTipo = emp.por_tipo_evento || {};
      sheet.addRow([
        emp.nombre_completo || '',
        emp.cargo || '',
        emp.proyecto || '',
        emp.total_registros ?? 0,
        parseFloat(emp.horas_efectuadas) || 0,
        parseFloat(emp.horas_descanso_medico) || 0,
        parseFloat(emp.horas_otros_conceptos) || 0,
        parseFloat(emp.promedio_horas_dia) || 0,
        ...EVENTOS_TAREOS.map((e) => porTipo[e.key] ?? 0),
        emp.tardanzas ?? 0,
        EVENTOS_TAREOS.reduce((s, e) => s + (porTipo[e.key] ?? 0), 0) + (emp.tardanzas ?? 0),
      ]);
    });

    const totales = resumen.empleados.reduce(
      (acc, emp) => {
        acc.registros += emp.total_registros ?? 0;
        acc.horas += parseFloat(emp.total_horas) || 0;
        acc.horasEfect += parseFloat(emp.horas_efectuadas) || 0;
        acc.horasDm += parseFloat(emp.horas_descanso_medico) || 0;
        acc.horasOtros += parseFloat(emp.horas_otros_conceptos) || 0;
        acc.tardanzas += emp.tardanzas ?? 0;
        const pt = emp.por_tipo_evento || {};
        acc.sumaEventos += EVENTOS_TAREOS.reduce((s, e) => s + (pt[e.key] ?? 0), 0) + (emp.tardanzas ?? 0);
        EVENTOS_TAREOS.forEach((e) => {
          acc.eventos[e.key] = (acc.eventos[e.key] || 0) + (pt[e.key] ?? 0);
        });
        return acc;
      },
      { registros: 0, horas: 0, horasEfect: 0, horasDm: 0, horasOtros: 0, tardanzas: 0, sumaEventos: 0, eventos: {} }
    );
    const promHoras = totales.registros > 0 ? totales.horas / totales.registros : 0;
    const totalesRow = sheet.addRow([
      'TOTALES',
      '',
      '',
      totales.registros,
      totales.horasEfect,
      totales.horasDm,
      totales.horasOtros,
      promHoras,
      ...EVENTOS_TAREOS.map((e) => totales.eventos[e.key] ?? 0),
      totales.tardanzas,
      totales.sumaEventos,
    ]);
    totalesRow.font = { bold: true };
    totalesRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F4F6' } };

    sheet.columns.forEach((col, i) => {
      let max = 12;
      if (i === 0) max = 35;
      if (i === 1) max = 25;
      if (i === 2) max = 30;
      col.width = max;
    });

    const buf = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Resumen_Tareos_${resumen.periodo?.fecha_inicio || 'inicio'}_${resumen.periodo?.fecha_fin || 'fin'}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!selectedCompany) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
        <p className="text-amber-800 font-medium">Selecciona una empresa para acceder a los reportes.</p>
        <p className="text-sm text-amber-700 mt-1">Usa "Cambiar empresa" en el menú lateral.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Reportes</h2>
        <p className="text-sm text-gray-500 mt-1">Análisis y reportes del sistema</p>
      </div>

      {/* Reporte: Resumen de Tareos por Fecha Personalizada */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-teal-50 to-cyan-50">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <span className="text-teal-600">📋</span>
            Resumen de Tareos por Fecha Personalizada
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Consulta por trabajador el resumen de tareos, horas trabajadas y conceptos en el rango de fechas que elijas.
          </p>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Fecha desde</label>
              <input
                type="date"
                value={fechaDesde}
                onChange={(e) => setFechaDesde(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Fecha hasta</label>
              <input
                type="date"
                value={fechaHasta}
                onChange={(e) => setFechaHasta(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
            <button
              type="button"
              onClick={handleGenerarResumenTareos}
              disabled={loading}
              className="px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Generando...' : 'Generar reporte'}
            </button>
            {resumen && resumen.empleados && resumen.empleados.length > 0 && (
              <button
                type="button"
                onClick={exportarResumenExcel}
                className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 inline-flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                Exportar a Excel
              </button>
            )}
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          {resumen && (
            <div className="space-y-6 pt-4 border-t border-gray-200">
              {/* Período y totales */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-medium text-gray-500 uppercase">Período</p>
                  <p className="text-sm font-semibold text-gray-900 mt-1">
                    {resumen.periodo?.fecha_inicio} — {resumen.periodo?.fecha_fin}
                  </p>
                  <p className="text-xs text-gray-500">{resumen.periodo?.dias_periodo} días</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-medium text-gray-500 uppercase">Registros</p>
                  <p className="text-lg font-semibold text-gray-900">{resumen.totales?.total_registros ?? 0}</p>
                  <p className="text-xs text-gray-500">{resumen.totales?.total_dias_trabajados ?? 0} días asistidos</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-medium text-gray-500 uppercase">Horas trabajadas</p>
                  <p className="text-lg font-semibold text-teal-700">{formatNumber(resumen.totales?.total_horas_trabajadas)} h</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Efectuadas {formatNumber(resumen.totales?.total_horas_efectuadas)} h · Desc. médico {formatNumber(resumen.totales?.total_horas_descanso_medico)} h · Otros {formatNumber(resumen.totales?.total_horas_otros_conceptos)} h
                  </p>
                  <p className="text-xs text-gray-500">Promedio {formatNumber(resumen.totales?.promedio_horas_dia)} h/día</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-medium text-gray-500 uppercase">Trabajadores</p>
                  <p className="text-lg font-semibold text-gray-900">{resumen.totales?.total_empleados ?? 0}</p>
                </div>
              </div>

              {/* Tabla por trabajador: todos los eventos mapeados (asistio, falta, descanso, feriado, permiso, etc.) */}
              <div>
                <h4 className="text-sm font-semibold text-gray-800 mb-3">Por trabajador</h4>
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase sticky left-0 bg-gray-50">Trabajador</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Cargo</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Proyecto</th>
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-600 uppercase">Registros</th>
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-600 uppercase whitespace-nowrap" title="Solo días marcados como Asistió">Horas efect.</th>
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-600 uppercase whitespace-nowrap" title="Descanso médico y subsidio por enfermedad (reglas de planilla)">Desc. méd. (h)</th>
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-600 uppercase whitespace-nowrap" title="Feriado laborado, descanso laborado y demás horas fuera de asistió">Otros (h)</th>
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-600 uppercase">Prom. h/día</th>
                        {EVENTOS_TAREOS.map((e) => (
                          <th key={e.key} className="px-2 py-2 text-center text-xs font-medium text-gray-600 uppercase whitespace-nowrap">{e.label}</th>
                        ))}
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-600 uppercase">Tardanzas</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 uppercase">Total conceptos</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {(!resumen.empleados || resumen.empleados.length === 0) ? (
                        <tr>
                          <td colSpan={EVENTOS_TAREOS.length + 10} className="px-4 py-8 text-center text-gray-500">
                            No hay registros de tareos en el período seleccionado.
                          </td>
                        </tr>
                      ) : (
                        <>
                          {resumen.empleados.map((emp) => {
                            const porTipo = emp.por_tipo_evento || {};
                            return (
                              <tr key={emp.empleado_id} className="hover:bg-gray-50">
                                <td className="px-3 py-2 font-medium text-gray-900 sticky left-0 bg-white">{emp.nombre_completo || '—'}</td>
                                <td className="px-3 py-2 text-gray-600">{emp.cargo || '—'}</td>
                                <td className="px-3 py-2 text-gray-700 max-w-xs truncate" title={emp.proyecto || ''}>{emp.proyecto || '—'}</td>
                                <td className="px-2 py-2 text-center text-gray-700">{emp.total_registros ?? 0}</td>
                                <td className="px-2 py-2 text-center text-green-700 font-medium">{formatNumber(emp.horas_efectuadas)}</td>
                                <td className="px-2 py-2 text-center text-purple-700 font-medium">{formatNumber(emp.horas_descanso_medico)}</td>
                                <td className="px-2 py-2 text-center text-blue-700 font-medium">{formatNumber(emp.horas_otros_conceptos)}</td>
                                <td className="px-2 py-2 text-center text-gray-700">{formatNumber(emp.promedio_horas_dia)}</td>
                                {EVENTOS_TAREOS.map((e) => (
                                  <td key={e.key} className={`px-2 py-2 text-center ${e.class}`}>
                                    {porTipo[e.key] ?? 0}
                                  </td>
                                ))}
                                <td className="px-2 py-2 text-center text-amber-600">{emp.tardanzas ?? 0}</td>
                                <td className="px-3 py-2 text-right font-medium text-gray-900">
                                  {EVENTOS_TAREOS.reduce((s, e) => s + (porTipo[e.key] ?? 0), 0) + (emp.tardanzas ?? 0)}
                                </td>
                              </tr>
                            );
                          })}
                          {/* Fila TOTALES */}
                          {resumen.empleados.length > 0 && (() => {
                            const totales = resumen.empleados.reduce((acc, emp) => {
                              acc.registros += emp.total_registros ?? 0;
                              acc.horas += parseFloat(emp.total_horas) || 0;
                              acc.horasEfect += parseFloat(emp.horas_efectuadas) || 0;
                              acc.horasDm += parseFloat(emp.horas_descanso_medico) || 0;
                              acc.horasOtros += parseFloat(emp.horas_otros_conceptos) || 0;
                              acc.tardanzas += emp.tardanzas ?? 0;
                              const pt = emp.por_tipo_evento || {};
                              EVENTOS_TAREOS.forEach((e) => { acc.eventos[e.key] = (acc.eventos[e.key] || 0) + (pt[e.key] ?? 0); });
                              acc.sumaEventos += EVENTOS_TAREOS.reduce((s, e) => s + (pt[e.key] ?? 0), 0) + (emp.tardanzas ?? 0);
                              return acc;
                            }, { registros: 0, horas: 0, horasEfect: 0, horasDm: 0, horasOtros: 0, tardanzas: 0, sumaEventos: 0, eventos: {} });
                            const promHoras = totales.registros > 0 ? totales.horas / totales.registros : 0;
                            return (
                              <tr className="bg-gray-100 font-semibold border-t-2 border-gray-300">
                                <td className="px-3 py-2 text-gray-900 sticky left-0 bg-gray-100">TOTALES</td>
                                <td className="px-3 py-2 text-gray-500">—</td>
                                <td className="px-3 py-2 text-gray-500">—</td>
                                <td className="px-2 py-2 text-center text-gray-900">{totales.registros}</td>
                                <td className="px-2 py-2 text-center text-green-800">{formatNumber(totales.horasEfect)}</td>
                                <td className="px-2 py-2 text-center text-purple-800">{formatNumber(totales.horasDm)}</td>
                                <td className="px-2 py-2 text-center text-blue-800">{formatNumber(totales.horasOtros)}</td>
                                <td className="px-2 py-2 text-center text-gray-900">{formatNumber(promHoras)}</td>
                                {EVENTOS_TAREOS.map((e) => (
                                  <td key={e.key} className="px-2 py-2 text-center text-gray-800">
                                    {totales.eventos[e.key] ?? 0}
                                  </td>
                                ))}
                                <td className="px-2 py-2 text-center text-amber-700">{totales.tardanzas}</td>
                                <td className="px-3 py-2 text-right text-gray-900">{totales.sumaEventos}</td>
                              </tr>
                            );
                          })()}
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

            </div>
          )}
        </div>
      </div>

      {/* Reporte: Resumen por género (apartado propio) */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-purple-50">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <span className="text-indigo-600">👥</span>
            Resumen por género
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Cantidad de hombres y mujeres con actividad (tareos) en el período que elijas.
          </p>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Fecha desde</label>
              <input
                type="date"
                value={fechaDesdeGenero}
                onChange={(e) => setFechaDesdeGenero(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Fecha hasta</label>
              <input
                type="date"
                value={fechaHastaGenero}
                onChange={(e) => setFechaHastaGenero(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <button
              type="button"
              onClick={handleGenerarResumenGenero}
              disabled={loadingGenero}
              className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingGenero ? 'Generando...' : 'Generar reporte'}
            </button>
            {resumenGenero != null && (
              <button
                type="button"
                onClick={exportarResumenGeneroExcel}
                className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 inline-flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                Exportar a Excel
              </button>
            )}
          </div>

          {errorGenero && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {errorGenero}
            </div>
          )}

          {resumenGenero != null && (
            <div className="pt-4 border-t border-gray-200 space-y-6">
              <p className="text-sm text-gray-500">
                Período: {periodoGenero?.fecha_inicio ?? ''} — {periodoGenero?.fecha_fin ?? ''}
              </p>
              <div className="flex flex-wrap gap-6">
                <div className="bg-blue-50 rounded-lg px-5 py-4 min-w-[140px]">
                  <p className="text-xs font-medium text-blue-600 uppercase">Hombres</p>
                  <p className="text-2xl font-bold text-blue-800">{resumenGenero.hombres ?? 0}</p>
                </div>
                <div className="bg-pink-50 rounded-lg px-5 py-4 min-w-[140px]">
                  <p className="text-xs font-medium text-pink-600 uppercase">Mujeres</p>
                  <p className="text-2xl font-bold text-pink-800">{resumenGenero.mujeres ?? 0}</p>
                </div>
                {(resumenGenero.sin_dato ?? 0) > 0 && (
                  <div className="bg-gray-50 rounded-lg px-5 py-4 min-w-[140px]">
                    <p className="text-xs font-medium text-gray-600 uppercase">Sin especificar</p>
                    <p className="text-2xl font-bold text-gray-800">{resumenGenero.sin_dato}</p>
                  </div>
                )}
              </div>

              {/* Detalle: tablas por género (nombre, cargo, proyecto) */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {(resumenGenero.detalle?.hombres?.length ?? 0) > 0 && (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="px-4 py-2 bg-blue-50 border-b border-gray-200">
                      <h4 className="text-sm font-semibold text-blue-800">Hombres — detalle</h4>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Trabajador</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Cargo</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Proyecto</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {resumenGenero.detalle.hombres.map((row, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-3 py-2 font-medium text-gray-900">{row.nombre_completo || '—'}</td>
                              <td className="px-3 py-2 text-gray-600">{row.cargo || '—'}</td>
                              <td className="px-3 py-2 text-gray-700 max-w-xs truncate" title={row.proyecto || ''}>{row.proyecto || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                {(resumenGenero.detalle?.mujeres?.length ?? 0) > 0 && (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="px-4 py-2 bg-pink-50 border-b border-gray-200">
                      <h4 className="text-sm font-semibold text-pink-800">Mujeres — detalle</h4>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Trabajador</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Cargo</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Proyecto</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {resumenGenero.detalle.mujeres.map((row, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-3 py-2 font-medium text-gray-900">{row.nombre_completo || '—'}</td>
                              <td className="px-3 py-2 text-gray-600">{row.cargo || '—'}</td>
                              <td className="px-3 py-2 text-gray-700 max-w-xs truncate" title={row.proyecto || ''}>{row.proyecto || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
              {(resumenGenero.detalle?.sin_dato?.length ?? 0) > 0 && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-4 py-2 bg-gray-100 border-b border-gray-200">
                    <h4 className="text-sm font-semibold text-gray-800">Sin especificar — detalle</h4>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Trabajador</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Cargo</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Proyecto</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {resumenGenero.detalle.sin_dato.map((row, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-3 py-2 font-medium text-gray-900">{row.nombre_completo || '—'}</td>
                            <td className="px-3 py-2 text-gray-600">{row.cargo || '—'}</td>
                            <td className="px-3 py-2 text-gray-700 max-w-xs truncate" title={row.proyecto || ''}>{row.proyecto || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Reporte: Trabajadores activos — régimen pensionario */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-amber-50 to-orange-50">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <span className="text-amber-600">🏦</span>
            Trabajadores activos — régimen pensionario
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Listado de trabajadores con contrato vigente a la fecha de corte (según historial laboral) e indicación de sistema pensionario (ONP o AFP).
            Incluye planilla mensual y boleta liquidada. Excluye recibo por honorarios y formación laboral.
          </p>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Fecha de corte</label>
              <input
                type="date"
                value={fechaCortePension}
                onChange={(e) => setFechaCortePension(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              />
            </div>
            <button
              type="button"
              onClick={handleGenerarReportePension}
              disabled={loadingPension}
              className="px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingPension ? 'Generando...' : 'Generar reporte'}
            </button>
            {reportePension?.empleados?.length > 0 && (
              <button
                type="button"
                onClick={exportarReportePensionExcel}
                className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 inline-flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Exportar a Excel
              </button>
            )}
          </div>

          {errorPension && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {errorPension}
            </div>
          )}

          {reportePension && (
            <div className="pt-4 border-t border-gray-200 space-y-4">
              <p className="text-sm text-gray-500">
                Fecha de corte: <strong>{reportePension.fecha_corte}</strong>
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="bg-gray-50 rounded-lg px-5 py-4 min-w-[120px]">
                  <p className="text-xs font-medium text-gray-600 uppercase">Total activos</p>
                  <p className="text-2xl font-bold text-gray-900">{reportePension.totales?.total ?? 0}</p>
                </div>
                <div className="bg-sky-50 rounded-lg px-5 py-4 min-w-[120px]">
                  <p className="text-xs font-medium text-sky-600 uppercase">ONP</p>
                  <p className="text-2xl font-bold text-sky-800">{reportePension.totales?.onp ?? 0}</p>
                </div>
                <div className="bg-amber-50 rounded-lg px-5 py-4 min-w-[120px]">
                  <p className="text-xs font-medium text-amber-700 uppercase">AFP</p>
                  <p className="text-2xl font-bold text-amber-900">{reportePension.totales?.afp ?? 0}</p>
                </div>
                {(reportePension.totales?.sin_especificar ?? 0) > 0 && (
                  <div className="bg-red-50 rounded-lg px-5 py-4 min-w-[120px]">
                    <p className="text-xs font-medium text-red-600 uppercase">Sin especificar</p>
                    <p className="text-2xl font-bold text-red-800">{reportePension.totales.sin_especificar}</p>
                  </div>
                )}
              </div>

              {reportePension.empleados?.length > 0 ? (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50 sticky top-0 z-10">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase w-10">N°</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">DNI</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Trabajador</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Cargo</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Proyecto</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Régimen</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">AFP</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Tipo</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">CUSPP</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {reportePension.empleados.map((row, idx) => (
                          <tr key={row.id ?? idx} className="hover:bg-gray-50">
                            <td className="px-3 py-2 text-gray-500">{idx + 1}</td>
                            <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{row.numero_documento || '—'}</td>
                            <td className="px-3 py-2 font-medium text-gray-900">{row.nombre_completo || '—'}</td>
                            <td className="px-3 py-2 text-gray-600">{row.cargo || '—'}</td>
                            <td className="px-3 py-2 text-gray-700 max-w-xs truncate" title={row.proyecto || ''}>
                              {row.proyecto || '—'}
                            </td>
                            <td className="px-3 py-2">
                              <span
                                className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
                                  row.sistema_pensionario === 'AFP'
                                    ? 'bg-amber-100 text-amber-800'
                                    : row.sistema_pensionario === 'ONP'
                                      ? 'bg-sky-100 text-sky-800'
                                      : 'bg-gray-100 text-gray-700'
                                }`}
                              >
                                {row.sistema_pensionario || '—'}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-gray-600">{row.afp_nombre || '—'}</td>
                            <td className="px-3 py-2 text-gray-600">{row.tipo_afp || '—'}</td>
                            <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{row.cuspp || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-6">
                  No hay trabajadores activos con contrato vigente en la fecha indicada.
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Reporte: Conceptos de ingreso en boleta */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-green-50">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <span className="text-emerald-600">💰</span>
            Conceptos de ingreso en boleta
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Tabla dinámica: cada fila es un trabajador activo a la fecha de corte y cada columna un concepto de ingreso (código PLAME).
            Incluye columna <strong>Modalidad</strong>: Planilla (planilla mensual) o RH (recibo por honorarios).
          </p>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Fecha de corte</label>
              <input
                type="date"
                value={fechaCorteIngresos}
                onChange={(e) => setFechaCorteIngresos(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
            <button
              type="button"
              onClick={handleGenerarReporteIngresos}
              disabled={loadingIngresos}
              className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingIngresos ? 'Generando...' : 'Generar reporte'}
            </button>
            {reporteIngresos?.filas?.length > 0 && (
              <button
                type="button"
                onClick={exportarReporteIngresosExcel}
                className="px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 inline-flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Exportar a Excel
              </button>
            )}
          </div>

          {errorIngresos && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {errorIngresos}
            </div>
          )}

          {reporteIngresos && (
            <div className="pt-4 border-t border-gray-200 space-y-4">
              <p className="text-sm text-gray-500">
                Fecha de corte: <strong>{reporteIngresos.fecha_corte}</strong>
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="bg-gray-50 rounded-lg px-5 py-4 min-w-[120px]">
                  <p className="text-xs font-medium text-gray-600 uppercase">Trabajadores</p>
                  <p className="text-2xl font-bold text-gray-900">{reporteIngresos.totales?.empleados ?? 0}</p>
                </div>
                <div className="bg-blue-50 rounded-lg px-5 py-4 min-w-[100px]">
                  <p className="text-xs font-medium text-blue-700 uppercase">Planilla</p>
                  <p className="text-2xl font-bold text-blue-900">{reporteIngresos.totales?.empleados_planilla ?? 0}</p>
                </div>
                <div className="bg-violet-50 rounded-lg px-5 py-4 min-w-[100px]">
                  <p className="text-xs font-medium text-violet-700 uppercase">RH</p>
                  <p className="text-2xl font-bold text-violet-900">{reporteIngresos.totales?.empleados_rh ?? 0}</p>
                </div>
                <div className="bg-emerald-50 rounded-lg px-5 py-4 min-w-[120px]">
                  <p className="text-xs font-medium text-emerald-700 uppercase">Conceptos PLAME</p>
                  <p className="text-2xl font-bold text-emerald-900">
                    {reporteIngresos.totales?.conceptos_plame_distintos ?? 0}
                  </p>
                </div>
                <div className="bg-green-50 rounded-lg px-5 py-4 min-w-[120px]">
                  <p className="text-xs font-medium text-green-700 uppercase">Filas detalle</p>
                  <p className="text-2xl font-bold text-green-900">{reporteIngresos.totales?.filas_concepto ?? 0}</p>
                </div>
                {(reporteIngresos.totales?.empleados_sin_conceptos ?? 0) > 0 && (
                  <div className="bg-amber-50 rounded-lg px-5 py-4 min-w-[120px]">
                    <p className="text-xs font-medium text-amber-700 uppercase">Sin conceptos</p>
                    <p className="text-2xl font-bold text-amber-900">
                      {reporteIngresos.totales.empleados_sin_conceptos}
                    </p>
                  </div>
                )}
              </div>

              {pivotIngresos.filas?.length > 0 ? (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-4 py-2 bg-emerald-50/80 border-b border-gray-200 flex items-center justify-between gap-2">
                    <h4 className="text-sm font-semibold text-emerald-800">Tabla dinámica — montos por concepto</h4>
                    <span className="text-xs text-gray-500">Desplaza horizontalmente para ver todos los conceptos</span>
                  </div>
                  <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm border-collapse">
                      <thead className="bg-gray-50 sticky top-0 z-20">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase sticky left-0 z-30 bg-gray-50 border-r border-gray-200 min-w-[100px]">DNI</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase sticky left-[100px] z-30 bg-gray-50 border-r border-gray-200 min-w-[220px]">Trabajador</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase sticky left-[320px] z-30 bg-gray-50 border-r border-gray-200 min-w-[95px]">Modalidad</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase sticky left-[415px] z-30 bg-gray-50 border-r border-gray-200 min-w-[140px]">Régimen</th>
                          {pivotIngresos.columnas.map((col) => (
                            <th
                              key={col.key}
                              className="px-2 py-2 text-right text-xs font-medium text-gray-600 uppercase min-w-[120px] max-w-[160px]"
                              title={`${col.codigo_plame} — ${col.concepto_descripcion || ''}`}
                            >
                              <div className="font-mono text-emerald-800">{col.codigo_plame}</div>
                              <div className="font-normal normal-case text-[10px] leading-tight text-gray-500 line-clamp-2">
                                {col.concepto_descripcion || '—'}
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {pivotIngresos.filas.map((emp) => (
                          <tr key={emp.empleado_id} className={`hover:bg-gray-50 ${emp.sin_conceptos ? 'bg-amber-50/40' : ''}`}>
                            <td className="px-3 py-2 text-gray-700 whitespace-nowrap sticky left-0 z-10 bg-inherit border-r border-gray-100">{emp.numero_documento || '—'}</td>
                            <td className="px-3 py-2 sticky left-[100px] z-10 bg-inherit border-r border-gray-100 min-w-[220px]">
                              <div className="font-medium text-gray-900">{emp.nombre_completo || '—'}</div>
                              <div className="text-[10px] text-gray-500 truncate max-w-[200px]" title={emp.cargo || ''}>{emp.cargo || '—'}</div>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap sticky left-[320px] z-10 bg-inherit border-r border-gray-100">
                              <span
                                className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
                                  emp.es_recibo_honorarios || emp.modalidad_pago === 'RH'
                                    ? 'bg-violet-100 text-violet-800'
                                    : 'bg-blue-100 text-blue-800'
                                }`}
                              >
                                {emp.modalidad_pago || (emp.es_recibo_honorarios ? 'RH' : 'Planilla')}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-gray-600 text-xs whitespace-nowrap sticky left-[415px] z-10 bg-inherit border-r border-gray-200">
                              {emp.regimen_laboral_codigo || emp.regimen_laboral_nombre
                                ? `${emp.regimen_laboral_codigo || ''}${emp.regimen_laboral_nombre ? ` — ${emp.regimen_laboral_nombre}` : ''}`
                                : '—'}
                            </td>
                            {pivotIngresos.columnas.map((col) => {
                              const monto = emp.montos[col.key];
                              return (
                                <td key={col.key} className="px-2 py-2 text-right text-gray-800 whitespace-nowrap tabular-nums">
                                  {monto != null ? <span className="font-medium">{formatMonto(monto)}</span> : <span className="text-gray-300">—</span>}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-6">
                  No hay trabajadores activos con contrato vigente en la fecha indicada.
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Reporte: Contratos pendientes de firma */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-orange-50 to-amber-50">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <span className="text-orange-600">📝</span>
            Contratos pendientes de firma
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Personal activo con contratos registrados en el módulo de contratos que todavía no están firmados.
            Un trabajador puede aparecer más de una vez si tiene varios contratos pendientes.
          </p>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            <button
              type="button"
              onClick={handleGenerarContratosPendientesFirma}
              disabled={loadingContratosFirma}
              className="px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-lg hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingContratosFirma ? 'Generando...' : 'Generar reporte'}
            </button>
            {reporteContratosFirma?.filas?.length > 0 && (
              <button
                type="button"
                onClick={exportarContratosPendientesFirmaExcel}
                className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 inline-flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Exportar a Excel
              </button>
            )}
          </div>

          {errorContratosFirma && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {errorContratosFirma}
            </div>
          )}

          {reporteContratosFirma && (
            <div className="pt-4 border-t border-gray-200 space-y-4">
              <p className="text-sm text-gray-500">
                Fecha de corte: <strong>{formatearFecha(reporteContratosFirma.fecha_corte)}</strong>
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="bg-orange-50 rounded-lg px-5 py-4 min-w-[140px]">
                  <p className="text-xs font-medium text-orange-700 uppercase">Trabajadores</p>
                  <p className="text-2xl font-bold text-orange-900">
                    {reporteContratosFirma.totales?.trabajadores ?? 0}
                  </p>
                </div>
                <div className="bg-amber-50 rounded-lg px-5 py-4 min-w-[140px]">
                  <p className="text-xs font-medium text-amber-700 uppercase">Contratos pendientes</p>
                  <p className="text-2xl font-bold text-amber-900">
                    {reporteContratosFirma.totales?.contratos ?? 0}
                  </p>
                </div>
              </div>

              {reporteContratosFirma.filas?.length > 0 ? (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50 sticky top-0 z-10">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase w-10">N°</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Documento</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Nombre completo</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Cargo</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Ingreso</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Tipo de contrato</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Nombre del contrato</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Inicio</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Término</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Estado contrato</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Estado de firma</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {reporteContratosFirma.filas.map((row, idx) => (
                          <tr key={row.contrato_id ?? idx} className="hover:bg-gray-50">
                            <td className="px-3 py-2 text-gray-500">{idx + 1}</td>
                            <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                              {row.documento_identidad || row.numero_documento || '—'}
                            </td>
                            <td className="px-3 py-2 font-medium text-gray-900">{row.nombre_completo || '—'}</td>
                            <td className="px-3 py-2 text-gray-600">{row.cargo || '—'}</td>
                            <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{formatearFecha(row.fecha_ingreso)}</td>
                            <td className="px-3 py-2 text-gray-700">{row.tipo_contrato || '—'}</td>
                            <td className="px-3 py-2 text-gray-800 max-w-xs" title={row.titulo || ''}>{row.titulo || '—'}</td>
                            <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{formatearFecha(row.fecha_inicio)}</td>
                            <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                              {row.fecha_termino ? formatearFecha(row.fecha_termino) : '—'}
                            </td>
                            <td className="px-3 py-2">
                              <span
                                className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
                                  row.estado_contrato === 'Vigente'
                                    ? 'bg-green-100 text-green-800'
                                    : row.estado_contrato === 'Vencido'
                                      ? 'bg-red-100 text-red-800'
                                      : row.estado_contrato === 'Por iniciar'
                                        ? 'bg-sky-100 text-sky-800'
                                        : 'bg-gray-100 text-gray-700'
                                }`}
                              >
                                {row.estado_contrato || '—'}
                              </span>
                            </td>
                            <td className="px-3 py-2">
                              <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-800">
                                {row.estado_firma || 'Pendiente'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-6">
                  No hay contratos registrados pendientes de firma para el personal activo.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Reportes;
